import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, CircularProgress, Alert, Paper, Grid, List, ListItem, ListItemText, ListItemButton } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const TwilioNumberStep = ({ onNext, onBack, businessInfo }) => {
  const { user } = useAuth();
  const [areaCode, setAreaCode] = useState('');
  const [availableNumbers, setAvailableNumbers] = useState([]);
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Extract area code from business phone if available
  useEffect(() => {
    if (businessInfo?.phone) {
      const match = businessInfo.phone.match(/\((\d{3})\)/);
      if (match) {
        setAreaCode(match[1]);
      }
    }
  }, [businessInfo]);

  const searchNumbers = async () => {
    if (!areaCode || areaCode.length !== 3) {
      setError('Please enter a valid 3-digit area code');
      return;
    }

    setLoading(true);
    setError('');
    try {
      // Use the deployed Cloud Function endpoint
      const cloudFunctionUrl = `https://us-central1-auto-text-back-web-app.cloudfunctions.net/searchNumbers?areaCode=${areaCode}`;
      console.log('Calling Twilio search Cloud Function:', cloudFunctionUrl);

      const response = await axios.get(cloudFunctionUrl);

      if (response.data && response.data.success && Array.isArray(response.data.numbers)) {
        setAvailableNumbers(response.data.numbers);

        if (response.data.numbers.length === 0) {
          setError(`No available numbers found for area code ${areaCode}. Try a different one.`);
        } else {
          setError(null);
        }
      } else {
        const apiError = response.data?.error || 'Failed to fetch numbers. Unexpected response from server.';
        setError(apiError);
        setAvailableNumbers([]);
        console.error('API Error or Unexpected Response:', response.data);
      }
    } catch (err) {
      console.error('Error searching numbers:', err);
      const errMsg = err.response?.data?.error || err.message || 'An unexpected error occurred while searching for numbers.';
      setError(errMsg);
      setAvailableNumbers([]);
    } finally {
      setLoading(false);
    }
  };

  const purchaseNumber = async (number) => {
    setLoading(true);
    setError('');
    try {
      // Use the deployed Cloud Function endpoint
      const cloudFunctionUrl = 'https://us-central1-auto-text-back-web-app.cloudfunctions.net/purchaseNumber';
      console.log('Calling Twilio purchase Cloud Function:', cloudFunctionUrl);
      console.log('Purchasing number:', number, 'for business:', businessInfo.name, 'with ID:', businessInfo.id);

      const response = await axios.post(cloudFunctionUrl, {
        phoneNumber: number,
        businessName: businessInfo.name,
        businessId: businessInfo.id
      });

      if (response.data.success) {
        setSuccess('Phone number purchased successfully!');
        // Wait a moment to show success message
        setTimeout(() => {
          onNext({
            twilioNumber: response.data.phoneNumber
          });
        }, 1500);
      }
    } catch (err) {
      setError('Failed to purchase number. Please try again.');
      console.error('Error purchasing number:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 2 }}>
      <Typography variant="h5" gutterBottom>
        Select Your Business Line
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="body1" gutterBottom>
          We will set up a dedicated number for your missed calls. When you miss a call on your main business number, it will forward here. Our AI will automatically text your customer, answer their questions, and send the conversation back to your business line.
        </Typography>

        <Grid container spacing={2} alignItems="center" sx={{ mt: 2 }}>
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              label="Area Code"
              value={areaCode}
              onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, '').slice(0, 3))}
              placeholder="e.g., 415"
              disabled={loading}
              helperText="Enter a 3-digit area code"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              variant="contained"
              color="primary"
              onClick={searchNumbers}
              disabled={loading || areaCode.length !== 3}
              fullWidth
            >
              {loading ? <CircularProgress size={24} /> : 'Search Numbers'}
            </Button>
          </Grid>
        </Grid>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {success}
          </Alert>
        )}

        {availableNumbers.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Available Numbers
            </Typography>
            <List>
              {availableNumbers.map((number) => (
                <ListItem key={number.phoneNumber} disablePadding>
                  <ListItemButton
                    selected={selectedNumber === number.phoneNumber}
                    onClick={() => setSelectedNumber(number.phoneNumber)}
                    disabled={loading}
                  >
                    <ListItemText
                      primary={number.phoneNumber}
                      secondary={number.friendlyName}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button onClick={onBack} variant="outlined" disabled={loading}>
          Back
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={loading || !selectedNumber}
          onClick={() => purchaseNumber(selectedNumber)}
        >
          {loading ? <CircularProgress size={24} /> : 'Get This Number'}
        </Button>
      </Box>
    </Box>
  );
};

export default TwilioNumberStep;